// apps/admin/src/features/users/AdminUserActivitySection.tsx
//
// Phase 8.2 Wave 7 Task 50 — Activity section of the Admin User Detail.
//
// vg-binding: CONTEXT:decisions (P999.11.D-03/D-14/D-15/D-16 — features/ batch A dark-token migration)
// vg-binding: INTERFACE-STANDARDS:error-shape (N/A — pure FE restyle, no API surface)
// Paginated audit log table fed by GET /admin/users/:id/activity. Uses
// useInfiniteQuery with page_size=20 so the "Tải thêm" button appends new
// rows without resetting the list.
//
// vg-binding: PLAN:task-50
// vg-binding: P8.D-101  — login history (IP/device/timestamp), role changes,
//                          security events, last active pages — all surfaced
//                          here via the BE category enum
// vg-binding: P8.D-380  — audit entry produced by every mutation appears
//                          here on the next refetch

import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  getAdminUserActivity,
  type AdminUserActivityCategory,
  type AdminUserActivityEntry,
  type AdminUserActivityPage,
  type AdminUserApiError,
} from '../../api/admin-users.api.js';

interface AdminUserActivitySectionProps {
  userId: string;
}

const PAGE_SIZE = 20;

const CATEGORY_VALUES: AdminUserActivityCategory[] = [
  'all',
  'login',
  'role_change',
  'security',
  'page_visit',
];

const CATEGORY_BADGE: Record<string, string> = {
  login: 'bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300',
  role_change: 'bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-300',
  security: 'bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300',
  page_visit: 'bg-pw-surface text-pw-text',
};

// Localized short badge labels keyed on the raw BE category enum.
const CATEGORY_BADGE_KEY: Record<string, string> = {
  login: 'login',
  role_change: 'role_change',
  security: 'security',
  page_visit: 'page_visit',
};

// ---------------------------------------------------------------------------
// Event label resolution (dbg-20260731-025626)
// ---------------------------------------------------------------------------
//
// The Event column used to print `row.event_type` verbatim — the raw audit
// action enum (`AUTH_LOGIN`, `support_ticket_message.created`,
// `blueprint:create`), which reads as a key, not as an event. Labels for every
// action present in the audit store live under `detail.activity.events.*` in
// all three locales.

/**
 * Audit actions carry `.` and `:` separators, both of which i18next would treat
 * as key nesting — `billing.mode.switched` would be looked up as three levels
 * deep. The catalog keys are flattened the same way.
 */
function eventKey(action: string): string {
  return action.replace(/[.:]/g, '_');
}

/**
 * Last-resort label for an action with no catalog entry (a new audit action
 * shipped after this catalog). Turns the enum into a sentence rather than
 * leaking the token: `AUTH_TOKEN_REVOKED` → "Auth token revoked".
 *
 * Not translated on purpose — it is derived from a wire enum, like an id. Any
 * action that shows up here should be added to the catalog.
 */
function humanizeAction(action: string): string {
  const words = action
    .replace(/[.:_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  if (words.length === 0) return action;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function RoleDiff({
  diff,
}: {
  diff: AdminUserActivityEntry['role_diff'];
}): JSX.Element | null {
  if (!diff) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1 text-xs">
      {diff.added.map((r) => (
        <span
          key={`+${r}`}
          className="rounded bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 text-emerald-700 dark:text-emerald-300"
        >
          +{r}
        </span>
      ))}
      {diff.removed.map((r) => (
        <span
          key={`-${r}`}
          className="rounded bg-rose-50 dark:bg-rose-950/40 px-1.5 py-0.5 text-rose-700 dark:text-rose-300"
        >
          −{r}
        </span>
      ))}
    </div>
  );
}

export function AdminUserActivitySection({
  userId,
}: AdminUserActivitySectionProps): JSX.Element {
  const { t } = useTranslation('users');
  const [category, setCategory] = useState<AdminUserActivityCategory>('all');

  const query = useInfiniteQuery<
    AdminUserActivityPage,
    AdminUserApiError | Error
  >({
    queryKey: ['admin', 'users', userId, 'activity', { category }],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) =>
      getAdminUserActivity(userId, {
        page: pageParam as number,
        limit: PAGE_SIZE,
        category,
      }),
    getNextPageParam: (lastPage) => {
      const consumed = lastPage.meta.page * lastPage.meta.limit;
      if (consumed >= lastPage.meta.total) return undefined;
      return lastPage.meta.page + 1;
    },
  });

  const rows = query.data?.pages.flatMap((p) => p.rows) ?? [];
  const total = query.data?.pages[0]?.meta.total ?? 0;

  return (
    <div className="space-y-3" data-test-id="admin-user-activity-section">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-pw-text-muted">
          {t('detail.activity.totalPrefix')}{' '}
          <span className="font-medium text-pw-text">{total}</span>{' '}
          {t('detail.activity.totalSuffix')}
        </div>
        <Select
          value={category}
          onValueChange={(value) =>
            setCategory(value as AdminUserActivityCategory)
          }
        >
          <SelectTrigger
            data-test-id="admin-user-activity-category-filter"
            className="max-w-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORY_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`detail.activity.categoryOptions.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-md bg-pw-surface"
              aria-hidden
            />
          ))}
        </div>
      ) : query.isError ? (
        <div className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 p-4 text-sm text-rose-800 dark:text-rose-300">
          {t('detail.activity.loadError')}{' '}
          {(query.error as AdminUserApiError)?.userMessage ??
            (query.error as Error)?.message ??
            t('detail.unknownError')}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-pw-border p-6 text-center text-sm text-pw-text-muted">
          {t('detail.activity.empty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-pw-border">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-pw-border text-sm">
            <thead className="bg-pw-surface text-left text-xs font-medium uppercase tracking-wide text-pw-text-muted">
              <tr>
                <th className="px-3 py-2">{t('detail.activity.columns.type')}</th>
                <th className="px-3 py-2">{t('detail.activity.columns.event')}</th>
                <th className="px-3 py-2">{t('detail.activity.columns.detail')}</th>
                <th className="px-3 py-2">{t('detail.activity.columns.ipDevice')}</th>
                <th className="px-3 py-2">{t('detail.activity.columns.time')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-pw-border bg-pw-bg">
              {rows.map((row) => (
                <tr key={row.id} data-test-id={`admin-user-activity-row-${row.id}`}>
                  <td className="px-3 py-2 align-top">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        CATEGORY_BADGE[row.category] ?? 'bg-pw-surface text-pw-text'
                      }`}
                    >
                      {CATEGORY_BADGE_KEY[row.category]
                        ? t(`detail.activity.categoryBadge.${row.category}`)
                        : row.category}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-top font-medium text-pw-text">
                    {/*
                      Localized label; `humanizeAction` only fires for an action
                      missing from the catalog, so the cell never shows the raw
                      enum. dbg-20260731-025626.
                    */}
                    {t(`detail.activity.events.${eventKey(row.event_type)}`, {
                      defaultValue: humanizeAction(row.event_type),
                    })}
                  </td>
                  <td className="px-3 py-2 align-top text-pw-text">
                    {row.target_type && (
                      <div className="text-xs text-pw-text-muted">
                        {t('detail.activity.targetLabel')}:{' '}
                        <span className="text-pw-text">{row.target_type}</span>
                        {row.target_id && (
                          <code className="ml-1">
                            {row.target_id.slice(0, 8)}…
                          </code>
                        )}
                      </div>
                    )}
                    {row.detail ? (
                      <div className="mt-0.5 break-all">{row.detail}</div>
                    ) : row.target_type || row.role_diff || row.target_page ? null : (
                      <span className="text-pw-text-muted">
                        {t('detail.activity.noDetail')}
                      </span>
                    )}
                    <RoleDiff diff={row.role_diff} />
                    {row.target_page && (
                      <div className="mt-1 text-xs text-pw-text-muted">
                        {t('detail.activity.pageLabel')}{' '}
                        <code>{row.target_page}</code>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-pw-text-muted">
                    {row.ip && <div>{row.ip}</div>}
                    {row.device && <div className="text-pw-text-muted">{row.device}</div>}
                  </td>
                  <td className="px-3 py-2 align-top text-xs text-pw-text-muted whitespace-nowrap">
                    {formatTimestamp(row.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {query.hasNextPage && (
        <div className="flex justify-center pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            data-test-id="admin-user-activity-load-more"
          >
            {query.isFetchingNextPage
              ? t('detail.activity.loadingMore')
              : t('detail.activity.loadMore', { count: PAGE_SIZE })}
          </Button>
        </div>
      )}
    </div>
  );
}
